import React, { useEffect, useState } from 'react';
import { PhoneIcon, ChevronDownIcon, XIcon } from './Icons';
import { motion, AnimatePresence } from 'framer-motion';
const navLinks = [{
  name: 'Home',
  href: '#home'
}, {
  name: 'The Problem',
  href: '#warning'
}, {
  name: 'Our Solution',
  href: '#solution'
}, {
  name: 'Services',
  href: '#services'
}, {
  name: 'Why Us',
  href: '#why-us'
}, {
  name: 'Service Areas',
  href: '#service-areas-map'
}, {
  name: 'Results',
  href: '#transformation'
}, {
  name: 'Contact',
  href: '#contact'
}, {
  name: 'FAQ',
  href: '#faq'
}];
export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      // Determine if scrolled past top
      if (currentScrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
      // Hide header on scroll down, show on scroll up
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
        setIsMenuOpen(false); // Close menu when scrolling down
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  return <motion.header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`} initial={{
    y: 0
  }} animate={{
    y: isVisible ? 0 : '-100%'
  }} transition={{
    duration: 0.3,
    ease: 'easeInOut'
  }} data-id="element-145">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-146">
        <div className="flex justify-between items-center h-20" data-id="element-147">
          <div className="flex items-center gap-3" data-id="element-148">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 -ml-2 mr-1 text-forest hover:bg-forest/5 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-forest/20" aria-label="Toggle menu" data-id="element-149">
              {isMenuOpen ? <XIcon className="w-6 h-6" data-id="element-150" /> : <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6" data-id="element-151">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" data-id="element-152" />
                </svg>}
            </button>
            <a href="#home" onClick={e => scrollToSection(e, '#home')} className="flex items-center gap-3" data-id="element-153">
              <img src="https://cdn.magicpatterns.com/uploads/wKYvS1cjpX9gsAXzBpZUZc/Photoroom_20260408_085127.png" alt="South Coast Tree Care Logo" className="h-16 w-auto object-contain" data-id="element-154" />
              <div className="hidden sm:block" data-id="element-155">
                <h1 className="text-xl font-bold text-forest leading-tight" data-id="element-156">
                  South Coast
                </h1>
                <p className="text-sm font-semibold text-earth tracking-widest uppercase" data-id="element-157">
                  Tree Care
                </p>
              </div>
            </a>
          </div>

          <div className="flex items-center gap-4" data-id="element-158">
            <div className="hidden md:flex flex-col items-end mr-4" data-id="element-159">
              <span className="text-sm text-gray-500 font-medium" data-id="element-160">
                24/7 Emergency Service
              </span>
              <a href="tel:9093717021" className="text-lg font-bold text-forest hover:text-accent transition-colors" data-id="element-161">
                (909) 371-7021
              </a>
            </div>
            <a href="tel:9093717021" className="flex items-center gap-2 bg-accent hover:bg-[#b56408] text-white px-5 py-3 rounded-lg font-bold transition-colors shadow-lg shadow-accent/30" data-id="element-162">
              <PhoneIcon className="w-5 h-5" data-id="element-163" />
              <span className="hidden sm:inline" data-id="element-164">Call Now</span>
            </a>
          </div>
        </div>
      </div>

      {/* Dropdown Menu */}
      <AnimatePresence data-id="element-165">
        {isMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} className="bg-white border-t border-gray-100 shadow-xl overflow-hidden" data-id="element-166">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-167">
              <nav className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-id="element-168">
                {navLinks.map(link => <a key={link.name} href={link.href} onClick={e => scrollToSection(e, link.href)} className="flex items-center justify-between p-3 rounded-lg text-forest font-medium hover:bg-forest/5 hover:text-accent transition-colors" data-id="element-169">
                    {link.name}
                    <ChevronDownIcon className="w-4 h-4 -rotate-90 opacity-50" data-id="element-170" />
                  </a>)}
              </nav>
              <div className="mt-6 pt-6 border-t border-gray-100 md:hidden flex flex-col items-center gap-2" data-id="element-171">
                <span className="text-sm text-gray-500 font-medium" data-id="element-172">
                  24/7 Emergency Service
                </span>
                <a href="tel:9093717021" className="text-xl font-bold text-forest" data-id="element-173">
                  (909) 371-7021
                </a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.header>;
};